import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Check, Circle, Plus, Trash2, ListChecks, Calendar, RotateCcw, GripVertical } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import InfoBentoCard from "@ui/InfoBentoCard";
import AIObservationInlineCard from "@ui/AIObservationInlineCard";
import ObservationFlagIcon from "@ui/ObservationFlagIcon";
import useFirestoreDoc from "@data/hooks/useFirestoreDoc";
import useKeepInPlace from "@shared/hooks/useKeepInPlace";
import { useLatestAIReview } from "@features/chat/hooks/useLatestAIReview";
import { defaultPhases } from "@domain/project/projectData";

// Parse "1d", "2w", "1.5d", "3" (default days) → number of days. Returns null on parse failure.
function parseDuration(input) {
  if (input == null || input === "") return 0;
  const m = String(input).trim().match(/^(\d+(?:\.\d+)?)\s*([dw])?$/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  return (m[2] || "d").toLowerCase() === "w" ? n * 7 : n;
}

function formatDuration(days) {
  if (!days) return "";
  if (days >= 7 && days % 7 === 0) return `${days / 7}w`;
  return `${+days.toFixed(2)}d`;
}

function formatTally(days) {
  if (!days) return null;
  if (days >= 7) {
    const weeks = days / 7;
    return Number.isInteger(weeks) ? `${weeks}w` : `${weeks.toFixed(1)}w`;
  }
  return `${+days.toFixed(1)}d`;
}

function addWeeks(dateStr, weeks) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().split("T")[0];
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function weeksBetween(start, end) {
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  return Math.round((e - s) / (7 * 24 * 60 * 60 * 1000));
}

function recalcDates(phases, startDate) {
  let cursor = startDate;
  return phases.map((phase) => {
    const weeks = phase.durationWeeks || 4;
    const phaseStart = cursor;
    const phaseEnd = addDays(addWeeks(cursor, weeks), -1);
    cursor = addWeeks(cursor, weeks);
    return { ...phase, startDate: phaseStart, endDate: phaseEnd };
  });
}

export default function Timeline({ phases, setPhases }) {
  const [expanded, setExpanded] = useState(new Set([1]));
  const [newTaskName, setNewTaskName] = useState({});
  const [projectStart, setProjectStart] = useFirestoreDoc("project-start", phases[0]?.startDate || "2026-04-01");
  const { observationsFor } = useLatestAIReview();

  const scheduledPhases = recalcDates(phases, projectStart);
  const projectEnd = scheduledPhases[scheduledPhases.length - 1]?.endDate;
  const totalWeeks = weeksBetween(projectStart, projectEnd);

  const keepInPlace = useKeepInPlace();
  const toggleExpand = (id, e) => {
    keepInPlace(e?.currentTarget);
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  };

  const toggleTask = (phaseId, taskId) => {
    setPhases(
      phases.map((p) =>
        p.id === phaseId
          ? {
              ...p,
              tasks: p.tasks.map((t) =>
                t.id === taskId ? { ...t, completed: !t.completed } : t
              ),
            }
          : p
      )
    );
  };

  const updatePhaseStatus = (phaseId, status) => {
    setPhases(
      phases.map((p) => (p.id === phaseId ? { ...p, status } : p))
    );
  };

  const updateDuration = (phaseId, weeks) => {
    setPhases(
      phases.map((p) => (p.id === phaseId ? { ...p, durationWeeks: parseInt(weeks) || 1 } : p))
    );
  };

  const addTask = (phaseId) => {
    const name = newTaskName[phaseId]?.trim();
    if (!name) return;
    const newId = Date.now();
    setPhases(
      phases.map((p) =>
        p.id === phaseId
          ? { ...p, tasks: [...p.tasks, { id: newId, name, completed: false }] }
          : p
      )
    );
    setNewTaskName({ ...newTaskName, [phaseId]: "" });
  };

  const deleteTask = (phaseId, taskId) => {
    setPhases(
      phases.map((p) =>
        p.id === phaseId
          ? { ...p, tasks: p.tasks.filter((t) => t.id !== taskId) }
          : p
      )
    );
  };

  const updateTaskDuration = (phaseId, taskId, durationDays) => {
    setPhases(
      phases.map((p) =>
        p.id === phaseId
          ? {
              ...p,
              tasks: p.tasks.map((t) =>
                t.id === taskId ? { ...t, durationDays } : t
              ),
            }
          : p
      )
    );
  };

  const reorderTasks = (phaseId, activeId, overId) => {
    if (activeId === overId) return;
    setPhases(
      phases.map((p) => {
        if (p.id !== phaseId) return p;
        const oldIndex = p.tasks.findIndex((t) => t.id === activeId);
        const newIndex = p.tasks.findIndex((t) => t.id === overId);
        if (oldIndex < 0 || newIndex < 0) return p;
        return { ...p, tasks: arrayMove(p.tasks, oldIndex, newIndex) };
      })
    );
  };

  const totalTasks = phases.reduce((sum, p) => sum + p.tasks.length, 0);
  const completedTasks = phases.reduce(
    (sum, p) => sum + p.tasks.filter((t) => t.completed).length,
    0
  );

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <InfoBentoCard
        cornerGlow
        icon={<ListChecks className="w-4 h-4" />}
        accent="blue"
        title="Project Timeline"
        subtitle={`${completedTasks} of ${totalTasks} tasks completed`}
        className="mb-6"
      >
        <div className="flex items-center gap-6 mt-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Start Date</span>
            <input
              type="date"
              value={projectStart}
              onChange={(e) => setProjectStart(e.target.value)}
              className="text-xs font-mono text-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)' }}
            />
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Completion </span>
            <span className="text-sm font-mono font-semibold text-emerald-400">{formatDate(projectEnd)}</span>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Duration </span>
            <span className="text-sm font-mono font-semibold text-blue-400">{totalWeeks} weeks</span>
          </div>
          <button
            onClick={() => {
              if (window.confirm("Reset timeline to defaults?")) {
                setPhases(defaultPhases);
                setProjectStart(defaultPhases[0]?.startDate || "2026-04-01");
              }
            }}
            className="ml-auto flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-lg transition-colors text-gray-400 hover:text-gray-200"
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)' }}
            title="Reset to defaults"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
        </div>
      </InfoBentoCard>

      {/* Gantt-style overview */}
      <InfoBentoCard cornerGlow frostedGradient accent="purple" className="mb-6">
        <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Schedule Overview</p>
        <div className="space-y-1.5">
          {scheduledPhases.map((phase) => {
            const phaseStartOffset = weeksBetween(projectStart, phase.startDate);
            const leftPct = totalWeeks > 0 ? (phaseStartOffset / totalWeeks) * 100 : 0;
            const widthPct = totalWeeks > 0 ? (phase.durationWeeks / totalWeeks) * 100 : 0;
            const done = phase.tasks.filter((t) => t.completed).length;
            const total = phase.tasks.length;
            const phasePct = total > 0 ? (done / total) * 100 : 0;

            const barColor =
              phase.status === "completed" ? "bg-emerald-500/60" :
              phase.status === "in-progress" ? "bg-blue-500/60" :
              phase.status === "blocked" ? "bg-red-500/60" : "bg-gray-500/30";

            return (
              <div key={phase.id} className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-36 truncate flex-shrink-0">{phase.name}</span>
                <div className="flex-1 h-4 rounded relative" style={{ background: 'var(--bg-progress-track)' }}>
                  <div
                    className={`absolute h-4 rounded ${barColor}`}
                    style={{ left: `${leftPct}%`, width: `${Math.max(widthPct, 1)}%` }}
                  >
                    {phasePct > 0 && (
                      <div
                        className="absolute top-0 left-0 h-4 rounded bg-emerald-400/40"
                        style={{ width: `${phasePct}%` }}
                      />
                    )}
                  </div>
                </div>
                <span className="text-xs font-mono text-gray-500 w-10 text-right flex-shrink-0">{phase.durationWeeks}w</span>
              </div>
            );
          })}
        </div>
      </InfoBentoCard>

      {/* Phase details */}
      <div className="space-y-3">
        {scheduledPhases.map((phase, idx) => {
          const isOpen = expanded.has(phase.id);
          const done = phase.tasks.filter((t) => t.completed).length;
          const total = phase.tasks.length;
          const phasePct = total > 0 ? Math.round((done / total) * 100) : 0;

          const allTasksDone = total > 0 && done === total;
          const phaseAccent =
            allTasksDone || phase.status === "completed" ? "green" :
            phase.status === "in-progress" ? "blue" :
            phase.status === "blocked" ? "rose" : "blue";

          const tallyDays = phase.tasks.reduce((sum, t) => sum + (t.durationDays || 0), 0);
          const tallyLabel = formatTally(tallyDays);

          return (
            <InfoBentoCard
              key={phase.id}
              cornerGlow
              frostedGradient
              accent={phaseAccent}
              className={allTasksDone ? "phase-all-done" : ""}
            >
              {/* Phase Header */}
              <div
                className="flex flex-col md:flex-row md:items-center md:justify-between cursor-pointer -m-4 p-4 gap-3 md:gap-0"
                onClick={(e) => toggleExpand(phase.id, e)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold uppercase tracking-wider text-gray-200 truncate">
                        {phase.name}
                      </span>
                      {allTasksDone && phase.status !== "completed" && (
                        <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-emerald-400 bg-emerald-500/15 flex-shrink-0">
                          <Check className="w-3 h-3" /> All tasks done
                        </span>
                      )}
                      {observationsFor("phase", phase.id).length > 0 && (
                        <ObservationFlagIcon
                          observations={observationsFor("phase", phase.id)}
                          size="sm"
                        />
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {formatDate(phase.startDate)} → {formatDate(phase.endDate)}
                      <span className="ml-2 text-gray-600">({phase.durationWeeks} weeks)</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
                  <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="number"
                      min="1"
                      max="52"
                      value={phase.durationWeeks}
                      onChange={(e) => updateDuration(phase.id, e.target.value)}
                      className="w-12 text-center text-sm font-mono text-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)' }}
                    />
                    <span className="text-xs text-gray-500">wks</span>
                    {tallyLabel && (
                      <span
                        className="text-xs font-mono text-gray-500 ml-1"
                        title="Sum of per-task durations in this phase"
                      >
                        Σ {tallyLabel}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-16 md:w-20 rounded-full h-1.5" style={{ background: 'var(--bg-progress-track)' }}>
                      <div
                        className="bg-blue-500/60 h-1.5 rounded-full"
                        style={{ width: `${phasePct}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-300 font-mono font-semibold">
                      {done}/{total}
                    </span>
                  </div>
                  <select
                    value={phase.status}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updatePhaseStatus(phase.id, e.target.value)}
                    className="text-xs md:text-sm rounded-lg px-2 md:px-3 py-1.5 text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    style={{ background: 'var(--select-bg)', border: '1px solid var(--border-input)' }}
                  >
                    <option value="not-started">Not Started</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>
              </div>

              {/* Tasks */}
              {isOpen && (
                <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border-row)' }}>
                  {observationsFor("phase", phase.id).length > 0 && (
                    <div className="space-y-2 mb-3 pl-6">
                      {observationsFor("phase", phase.id).map((obs, i) => (
                        <AIObservationInlineCard
                          key={`obs-${i}`}
                          observation={obs}
                          entityLabel={`the ${phase.name} phase`}
                        />
                      ))}
                    </div>
                  )}
                  <SortableTaskList
                    phase={phase}
                    onReorder={(activeId, overId) => reorderTasks(phase.id, activeId, overId)}
                    onToggle={(taskId) => toggleTask(phase.id, taskId)}
                    onDelete={(taskId) => deleteTask(phase.id, taskId)}
                    onDurationChange={(taskId, days) => updateTaskDuration(phase.id, taskId, days)}
                  />
                  <div className="flex items-center gap-2 pl-6 pt-2">
                    <Plus className="w-3.5 h-3.5 text-gray-600" />
                    <input
                      type="text"
                      placeholder="Add a task..."
                      value={newTaskName[phase.id] || ""}
                      onChange={(e) =>
                        setNewTaskName({
                          ...newTaskName,
                          [phase.id]: e.target.value,
                        })
                      }
                      onKeyDown={(e) => e.key === "Enter" && addTask(phase.id)}
                      className="text-xs bg-transparent focus:outline-none py-1 flex-1 text-gray-300 placeholder-gray-600"
                      style={{ borderBottom: '1px solid var(--border-row)' }}
                    />
                  </div>
                </div>
              )}
            </InfoBentoCard>
          );
        })}
      </div>
    </div>
  );
}

function SortableTaskList({ phase, onReorder, onToggle, onDelete, onDurationChange }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) onReorder(active.id, over.id);
      }}
    >
      <SortableContext
        items={phase.tasks.map((t) => t.id)}
        strategy={verticalListSortingStrategy}
      >
        {phase.tasks.map((task) => (
          <SortableTask
            key={task.id}
            task={task}
            onToggle={() => onToggle(task.id)}
            onDelete={() => onDelete(task.id)}
            onDurationChange={(days) => onDurationChange(task.id, days)}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableTask({ task, onToggle, onDelete, onDurationChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : "auto",
    position: "relative",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between py-1.5 pl-1 pr-1 group"
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <button
          {...attributes}
          {...listeners}
          className="touch-none cursor-grab active:cursor-grabbing text-gray-700 hover:text-gray-400 transition-colors"
          aria-label="Drag to reorder task"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <div
          className="flex items-center gap-2 cursor-pointer min-w-0 flex-1"
          onClick={onToggle}
        >
          {task.completed ? (
            <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
          ) : (
            <Circle className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
          )}
          <span
            className={`text-xs truncate ${
              task.completed ? "line-through text-gray-600" : "text-gray-300"
            }`}
          >
            {task.name}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <DurationInput
          value={task.durationDays || 0}
          onChange={onDurationChange}
        />
        <button
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 transition-opacity"
          aria-label="Delete task"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function DurationInput({ value, onChange }) {
  const [text, setText] = useState(formatDuration(value));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setText(formatDuration(value));
    setInvalid(false);
  }, [value]);

  const commit = () => {
    const trimmed = text.trim();
    if (trimmed === "") {
      if (value !== 0) onChange(0);
      setInvalid(false);
      return;
    }
    const parsed = parseDuration(trimmed);
    if (parsed === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setText(formatDuration(parsed));
    if (parsed !== value) onChange(parsed);
  };

  return (
    <input
      type="text"
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        if (invalid) setInvalid(false);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setText(formatDuration(value));
          setInvalid(false);
          e.currentTarget.blur();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      placeholder="—"
      title='Enter task duration. Use "1d" for 1 day or "1w" for 1 week.'
      className={`w-12 text-center text-xs font-mono rounded px-1 py-0.5 focus:outline-none focus:ring-1 ${
        invalid
          ? "ring-1 ring-red-500 text-red-400"
          : "text-gray-400 focus:ring-indigo-500"
      }`}
      style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)" }}
    />
  );
}
